import { css } from "@emotion/react";
import { useCallback, useState } from "react";
import { AlertMessageType } from "../../Alert";
import { CSVImportUIColumns, CellError, Mode } from "../CSVImportUI";
import { CSVImportUITable } from "../CSVImportUITable";
import { ScreenLayout } from "../ScreenLayout";
import { SubmitProgress } from "../SubmitProgress";
import { Typography } from "../../Typography";
import { UploadCsvButton } from "../UploadCsvButton";
import { sequence } from "../../@utils/sequence";
import { standardValidator } from "../standardValidator";
import { useNavigate } from "../ScreenProvider";
import { ScreenProps } from "./types";

export interface LoadCSVScreenProps<Columns, Row> extends ScreenProps {
  columns: Columns;
  data: Row[];
  headers: string[];
  errors: ([string, string][] | undefined)[];
  csvParser: (args: {
    file: File;
  }) => Promise<{ data?: Record<string, string>[]; error?: string }>;
  csvValidator?: (args: {
    row: Record<string, string>;
  }) => Promise<{ errors?: CellError[] }>;
  mode: Mode;
  /**
   * アップロード画面に進む前にトリガーされる。
   * 行ごとにデータを処理する。
   */
  onPreUploadDataByRow?: (args: {
    index: number;
    /**
     * 欠損したフィールドがある可能性があるため、型を緩くする。
     */
    row: Partial<Row>;
    mode: Mode;
  }) => Promise<{
    /**
     * @deprecated 代わりに `errors` を使用してください。
     */
    error?: CellError;
    errors?: CellError[];
    data?: Record<string, string>;
  }>;
  /**
   * アップロード画面に進む前にトリガーされる。
   * 一括でデータをアップロードする。
   */
  onPreUploadData?: (args: { data: Partial<Row>[]; mode: Mode }) => Promise<{
    errorMessage?: AlertMessageType;
    data?: Record<string, string>[];
  }>;
  onUpdateData: (data: Row[], errors: (CellError[] | undefined)[]) => void;
  title?: string;
}

export const LoadCSVScreen = <
  Columns extends CSVImportUIColumns,
  Row extends Record<string, string>,
>({
  data,
  headers,
  errors,
  csvParser,
  columns,
  csvValidator,
  onUpdateData,
  onPreUploadData,
  onPreUploadDataByRow,
  mode,
  title = "CSVの読み込み",
}: LoadCSVScreenProps<Columns, Row>) => {
  const { onNavigate } = useNavigate();
  const [error, setError] = useState<string>();

  const handlePreUpload = useCallback(
    async (data: Row[]) => {
      const result = await onPreUploadData?.({ data, mode });
      return result ?? {};
    },
    [mode, onPreUploadData],
  );

  const handlePreUploadByRow = useCallback(
    async (index: number, row: Row) => {
      const result = await onPreUploadDataByRow?.({
        index,
        row,
        mode,
      });
      return result ?? {};
    },
    [mode, onPreUploadDataByRow],
  );

  const handleUploadCSV = useCallback(
    async (file: File) => {
      const { data = [], error } = await csvParser({
        file,
      });

      setError(error);
      if (error) {
        return;
      }

      const errors: (CellError[] | undefined)[] = [];

      await sequence(
        data.map((row) => async () => {
          /**
           * カスタムバリデーターを先行して実行して、エラーがあれば標準のバリデーションは飛ばす。
           */
          const result = await csvValidator?.({ row });
          if (result?.errors && result.errors.length > 0) {
            errors.push(result.errors);
            return;
          }

          const error = Object.values(columns)
            .map((column) =>
              // 値が存在しない場合、validatorには空文字列を渡す
              standardValidator(column, row[column.label] ?? ""),
            )
            .filter((error): error is CellError => error !== undefined);

          errors.push(error.length > 0 ? error : undefined);
        }),
      );

      onUpdateData(data as Row[], errors);
    },
    [columns, csvParser, csvValidator, onUpdateData],
  );
  return (
    <ScreenLayout title={title}>
      <div
        css={css`
          display: grid;
          gap: 16px;
        `}
      >
        <div
          css={css`
            display: grid;
            gap: 8px;
          `}
        >
          <UploadCsvButton onChange={handleUploadCSV} />
          <Typography color="error" variant="caption" as="p">
            {error}
          </Typography>
        </div>
        {data.length > 0 ? (
          <CSVImportUITable
            columns={columns}
            rows={data}
            headers={headers}
            errors={errors}
          />
        ) : null}
        <div
          css={css`
            display: flex;
            justify-content: flex-end;
          `}
        >
          <SubmitProgress
            mode={onPreUploadData ? "all" : "byRow"}
            label="アップロードに進む"
            data={data}
            errors={errors}
            onSubmitComplete={onUpdateData}
            onSubmit={handlePreUpload}
            onSubmitByRow={handlePreUploadByRow}
            onFinish={() => {
              onNavigate("uploadCSV");
            }}
          />
        </div>
      </div>
    </ScreenLayout>
  );
};
